import {
  Box,
  Tabs,
  IconButton,
  Paper,
  Typography,
  Slide,
  Drawer,
  useMediaQuery,
  TextField,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useState, useEffect, useContext } from 'react'
import SettingsActionBar from './settingsMenu/SettingsActionBar'
import TradeExitSettings from './settingsMenu/sections/TradeExitSettings'
import BacktestSettings from './settingsMenu/sections/BacktestSettings'
import AccountSettings from './settingsMenu/sections/AccountSettings'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import SettingsIcon from '@mui/icons-material/Settings'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import BacktestReportCard from '../components/backtests/BacktestReportCard'
import VirtuosoListComponent from '../components/backtests/VirtuosoListComponent'
import { VirtuosoGrid } from 'react-virtuoso'
import { SimpleReportTable } from '../app/reports/components/backtestResults/BacktestTable'
import Tab from '@mui/material/Tab'
import PatternConditionReadView from '../app/alerts/components/PatternConditionReadView'
import PermissionOverlay from '../components/PermissionOverlay'
import { useApplicationSuiteContext } from '../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../contexts/SnackbarHelperContext'
import { usePattern } from '../hooks/usePattern'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../common/firebaseConfig'
import { CustomWidthTooltip, AdminInfoTooltipContent } from '../components/AdminInfoTooltip'
import { BacktestFolder } from '../hooks/useBacktestFolders'

// Tab Panel component
interface TabPanelProps {
  children?: React.ReactNode
  value: string
  name: string
  [other: string]: any
}

function TabPanel(props: TabPanelProps) {
  const { children, value, name, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== name}
      id={`backtest-tabpanel-${name}`}
      aria-labelledby={`backtest-tab-${name}`}
      style={{ height: '100%', overflow: 'auto' }}
      {...other}
    >
      {value === name && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  )
}

function BacktestPanelContainer({
  backtest,
  onClose,
  onView,
  folders,
  onMoveToFolder,
}: {
  backtest: any
  onClose: () => void
  onView: () => void
  folders?: BacktestFolder[]
  onMoveToFolder?: (folderId: string | null) => void | Promise<void>
}) {
  const {
    usermeta,
    canShowcaseViewBacktestSettings,
    canViewPrivatePatterns,
    backtestNameOverrides,
    renameBacktestLocally,
    tradingHouse,
  } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [exitPattern] = usePattern(
    backtest?.backtestSettings?.exitPatternId,
    backtest?.backtestSettings?.exitPatternVersion,
    backtest?.backtestSettings?.gcid || backtest?.gcid
  )

  const [currentTab, setCurrentTab] = useState('Settings')
  const theme = useTheme()
  const [mounted, setMounted] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [renameBacktestCallable, renameBacktestLoading] = useHttpsCallable(
    firebase_functions,
    'renameBacktest'
  )
  const [setBacktestFeaturedCallable, settingFeatured] = useHttpsCallable(
    firebase_functions,
    'adminSetBacktestFeatured'
  )
  const [featuredOverride, setFeaturedOverride] = useState<boolean | null>(null)
  const [getBacktests] = useHttpsCallable(firebase_functions, 'getBacktests')
  const [sessionBacktests, setSessionBacktests] = useState<any[]>([])
  const [sessionLoading, setSessionLoading] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Handle mounting for animation
  useEffect(() => {
    if (backtest) {
      setMounted(true)
    }
  }, [backtest])

  useEffect(() => {
    setCurrentTab('Settings')
    setSessionBacktests([])
    setFeaturedOverride(null)
  }, [backtest?.id])

  const handleTabChange = (_: any, newTab: string) => {
    setCurrentTab(newTab)
    if (newTab === 'Session' && sessionBacktests.length === 0 && backtest?.agentSessionId) {
      setSessionLoading(true)
      getBacktests({
        tradingHouse,
        type: 'search',
        backtestFilters: { agentSessionId: backtest.agentSessionId },
        take: 100,
        skip: 0,
      })
        .then((result: any) => {
          const data: any[] = result?.data?.data ?? []
          setSessionBacktests([...data].sort((a, b) => (b.startTime || 0) - (a.startTime || 0)))
        })
        .finally(() => setSessionLoading(false))
    }
  }

  if (!backtest) {
    return null
  }

  // Handle transition end
  const handleTransitionEnd = () => {
    if (!backtest) {
      setMounted(false)
    }
  }

  if (!mounted) return null

  const isFeatured = featuredOverride ?? !!backtest.isFeatured

  const handleToggleFeatured = async () => {
    const nextValue = !isFeatured
    try {
      const res: any = await setBacktestFeaturedCallable({
        backtestId: backtest.id,
        featured: nextValue,
      })
      if (!res?.data?.status || res.data.status >= 400) {
        setSnackbarSettings({
          message: res?.data?.message || 'Failed to update featured status.',
          severity: 'error',
          autoHideDuration: 6000,
        })
        return
      }
      setFeaturedOverride(nextValue)
      setSnackbarSettings({
        message: nextValue ? 'Added to Basic list.' : 'Removed from Basic list.',
        severity: 'success',
        autoHideDuration: 4000,
      })
    } catch (error) {
      setSnackbarSettings({
        message: 'Failed to update featured status.',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
  }

  const iconHeader = () => {
    if (usermeta.isAdmin) {
      const coreToggle = (
        <IconButton
          size="small"
          disabled={settingFeatured}
          onClick={handleToggleFeatured}
          title={isFeatured ? 'Remove from Basic list' : 'Add to Basic list'}
        >
          {settingFeatured ? (
            <CircularProgress size={16} />
          ) : isFeatured ? (
            <StarIcon fontSize="small" color="warning" />
          ) : (
            <StarBorderIcon fontSize="small" color="warning" />
          )}
        </IconButton>
      )
      return (
        <CustomWidthTooltip
          arrow={true}
          placement={'top-start'}
          title={
            <AdminInfoTooltipContent
              title="Admin Only"
              action={coreToggle}
              rows={[
                { label: 'Backtest', value: backtest.id },
                { label: 'Pattern', value: backtest.pattern?.id },
                { label: 'Runner', value: backtest.gcid },
                { label: 'Owner', value: backtest.backtestSettings?.gcid },
              ]}
            />
          }
        >
          <IconButton onClick={() => console.log(backtest)}>
            <AdminPanelSettings color={'warning'} />
          </IconButton>
        </CustomWidthTooltip>
      )
    } else {
      return <AssessmentIcon />
    }
  }

  // Content to display in both drawer and slide
  const panelContent = (
    <Paper
      elevation={4}
      sx={{
        position: 'relative',
        width: isMobile ? '100%' : 400,
        height: '100%',
        backgroundColor: theme.palette.background.custom2,
        borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(180deg, ${theme.palette.background.custom4} 0%, ${theme.palette.background.custom2} 100%)`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {iconHeader()}
          {isEditingName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
              <TextField
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                size="small"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    renameBacktestCallable({ backtestId: backtest.id, name: editName }).then(() => {
                      renameBacktestLocally(backtest.id, editName)
                      setIsEditingName(false)
                    })
                  } else if (e.key === 'Escape') {
                    setIsEditingName(false)
                  }
                }}
                sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: '0.9rem' } }}
              />
              <IconButton
                size="small"
                disabled={renameBacktestLoading}
                onClick={() =>
                  renameBacktestCallable({ backtestId: backtest.id, name: editName }).then(() => {
                    renameBacktestLocally(backtest.id, editName)
                    setIsEditingName(false)
                  })
                }
              >
                {renameBacktestLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CheckIcon fontSize="small" />
                )}
              </IconButton>
              <IconButton size="small" onClick={() => setIsEditingName(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  fontSize: '1rem',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {backtestNameOverrides[backtest.id] ?? backtest.backtestSettings.name}
              </Typography>
              {backtest.gcid === usermeta?.gcid && (
                <IconButton
                  size="small"
                  sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
                  onClick={() => {
                    setEditName(
                      backtestNameOverrides[backtest.id] ?? backtest.backtestSettings.name
                    )
                    setIsEditingName(true)
                  }}
                >
                  <EditIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
        <IconButton
          onClick={() => onClose()}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Backtest Tabs"
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            value="Settings"
            label="Settings"
            icon={<SettingsIcon />}
            iconPosition="start"
            data-tour-id="backtest-side-navigation-tab-settings"
          />
          <Tab
            value="Reports"
            label="Reports"
            icon={<AssessmentIcon />}
            iconPosition="start"
            data-tour-id="backtest-side-navigation-tab-reports"
          />
          <Tab
            value="Pattern"
            label="Pattern"
            icon={<AccountTreeIcon />}
            iconPosition="start"
            data-tour-id="backtest-side-navigation-tab-pattern"
          />
          {backtest.source === 'strategy-agent' && backtest.agentSessionId && (
            <Tab
              value="Session"
              label="Session"
              icon={<SmartToyOutlinedIcon />}
              iconPosition="start"
            />
          )}
        </Tabs>
      </Box>

      <PermissionOverlay
        isVisible={
          ((currentTab === 'Settings' || currentTab === 'Reports') &&
            !canShowcaseViewBacktestSettings &&
            backtest.gcid !== usermeta.gcid) ||
          (currentTab === 'Pattern' &&
            !canViewPrivatePatterns &&
            backtest.backtestSettings.gcid !== usermeta.gcid)
        }
        message="Upgrade to view details"
      >
        {/* Tab Panels */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', height: '100%' }}>
          <TabPanel value={currentTab} name="Reports">
            <SimpleReportTable backtest={backtest} />
          </TabPanel>
          <TabPanel value={currentTab} name="Settings">
            <Box sx={{ p: 2 }}>
              <BacktestSettings backtest={backtest} />
              {/* REMOVING SO THAT PERMISSIONOVERLAY RENDERS CORRECTLY WITHOUT SCROLL  */}
              {!canShowcaseViewBacktestSettings && backtest.gcid !== usermeta.gcid ? null : (
                <AccountSettings backtest={backtest} />
              )}
              <TradeExitSettings backtest={backtest} />
            </Box>
          </TabPanel>
          <TabPanel
            value={currentTab}
            name="Session"
            style={{ height: '100%', overflow: 'hidden' }}
          >
            {sessionLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={120}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <VirtuosoGrid
                totalCount={sessionBacktests.length}
                style={{ height: '100%', width: '100%' }}
                components={{ List: VirtuosoListComponent }}
                itemContent={(index) => {
                  const bt = sessionBacktests[index]
                  return (
                    <BacktestReportCard
                      backtest={bt}
                      condensed={false}
                      isActive={false}
                      footerActions={[]}
                    />
                  )
                }}
              />
            )}
          </TabPanel>
          <TabPanel value={currentTab} name="Pattern">
            <Box sx={{ p: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Entry Pattern
              </Typography>
              <PatternConditionReadView
                pattern={{
                  ...backtest.pattern!,
                  trigger: {
                    ...backtest.pattern!.trigger,
                    chartDetails: {
                      ...backtest.pattern!.trigger.chartDetails,
                      symbol: backtest.backtestSettings.executionSymbol,
                    },
                  },
                }}
                liveStatus={false}
              />
              {exitPattern && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Exit Pattern
                  </Typography>
                  <PatternConditionReadView
                    pattern={{
                      ...exitPattern,
                      trigger: {
                        ...exitPattern!.trigger,
                        chartDetails: {
                          ...exitPattern!.trigger.chartDetails,
                          symbol: backtest.backtestSettings.executionSymbol,
                        },
                      },
                    }}
                    liveStatus={false}
                  />
                </>
              )}
            </Box>
          </TabPanel>
        </Box>
      </PermissionOverlay>

      {/* Fixed Button Container */}
      <SettingsActionBar
        backtest={backtest}
        canView
        onView={onView}
        goBack={onClose}
        folders={folders}
        onMoveToFolder={onMoveToFolder}
      />
    </Paper>
  )

  // Render as Drawer on mobile, Slide on desktop
  return isMobile ? (
    <Drawer
      anchor="right"
      open={!!backtest}
      onClose={() => onClose()}
      PaperProps={{
        sx: {
          width: '400px',
          maxWidth: '100%',
          height: '100%',
        },
      }}
    >
      {panelContent}
    </Drawer>
  ) : (
    <Slide
      direction="left"
      in={!!backtest}
      mountOnEnter
      unmountOnExit
      onTransitionEnd={handleTransitionEnd}
    >
      {panelContent}
    </Slide>
  )
}

export default BacktestPanelContainer
